import { useState, useEffect } from 'react';
import _ from 'lodash';

import { apiList } from '../api/callAPI';
import useGlobalStatus from '../hook/useGlobalStatus';
import { MODAL_CREATE_REPORT } from '../components/modal/constants';
import { MEDIA_DATA } from '../constants/mediaData';

import useApiError from './useApiError';
import useAllAdAccount from './useAllAdAccount';
import useReportStatus from './useReportStatus';

// Get cid list, campaigns and custom conversions.

const getSelectedCidData = (allCidList, selectedCidList) => {
  return _.filter(allCidList, el => _.findIndex(selectedCidList, c => c.adAccountId === el.adAccountId) > -1);
}

const getSelectedCampaign = (allCampaign, selectedCampaign) => {
  return _.filter(allCampaign, el => _.findIndex(selectedCampaign, c => c === el.id) > -1);
}

const useReportData = (media) => {
  const { currentActiveData, entryPath, modalContent, setModalContent, isModalOpen, setIsModalOpen, } = useGlobalStatus();
  const { handleApiErrorStatus } = useApiError();
  const { allAdAccount } = useAllAdAccount();
  const { isLoading, setIsLoading } = useReportStatus();
  const [cidList, setCidList] = useState([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState([]);
  const [origiCustomData, setOrigiCustomData] = useState([]);
  const [selectedCustomFields, setSelectedCustomFields] = useState([]);
  const [selectedOptionFields, setSelectedOptionFields] = useState([]);
  const [isDataReady, setIsDataReady] = useState({
    cidList: false,
    campaignList: false,
    customConversions: false,
  });

  const updateIsLoading = () => {
    const { cidList, campaignList, customConversions } = isDataReady;

    if (cidList && campaignList && customConversions) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  };

  useEffect(() => {
    updateIsLoading();
  }, [isDataReady, entryPath]);

  useEffect(() => {
    const initializeCidList = () => {
      if (allAdAccount) {
        setCidList(allAdAccount.filter((el) => el.media === media));
        setSelectedAdAccounts(
          entryPath.includes('create') ? [] : getSelectedCidData(allAdAccount, currentActiveData.facebookAccounts)
        );
        setIsDataReady(prev =>
          entryPath.includes('create') ? ({ ...prev, cidList: true, campaignList: true, customConversions: true })
            : ({ ...prev, cidList: true })
        );
      }
    };

    initializeCidList();
  }, [allAdAccount, media, entryPath, currentActiveData])

  const fetchData = (apiFn, key, onSuccess) => {
    setIsLoading(true);
    setIsDataReady(prev => ({ ...prev, [key]: false }));

    apiFn({ cid: selectedAdAccounts.map(el => el.adAccountId).toString() })
      .then(res => {
        if (res.status === 200) {
          const resData = res.data.result;
          onSuccess(resData);
        } else {
          handleApiErrorStatus(res);
          setIsLoading(false);
        }
      })
  }

  const handleCampaignData = async (resData, type) => {
    let listData = [];
    let selectedData = [];

    const showModal = (resData) => {
      setModalContent((!resData?.length)
        ? { ...MODAL_CREATE_REPORT['checkNoRunningOrPausedCampaign'], media: media }
        : { ...MODAL_CREATE_REPORT['checkImportCampaign'], media: media });
      setIsModalOpen(true);
      setIsLoading(false);
    };

    if (type === 'click') {
      showModal(resData);
    }

    selectedData = getSelectedCampaign(resData, currentActiveData.campaignList);

    if (resData?.length) {
      listData = resData;
    }

    setCampaignList(listData);
    setSelectedCampaign(selectedData);
  }

  useEffect(() => {
    if (campaignList.length > 0) {
      setIsDataReady((prev) => ({ ...prev, campaignList: true }));
    }
  }, [campaignList]);

  const handleCustomData = (resData, type) => {
    const excludeTerms = [': CVR', ': Value', ': ROAS'];
    const filterResData = resData.filter(el => !excludeTerms.some(term => el.name.includes(term)));
    let customData = [];

    if (filterResData?.length) {
      customData = _.unionBy(filterResData, 'id');
    }

    if (entryPath.includes('edit')) {
      const updateFields = _.filter(currentActiveData.fields, field => {
        if (field.cid === null) {
          return true;
        }

        if (field.cid !== null) {
          const matchingCustomData = _.find(resData, { id: field.expression });
          return matchingCustomData;
        }

        return false;
      });

      setSelectedOptionFields(updateFields);
    }

    setOrigiCustomData(customData);

    if (type === 'click') {
      setModalContent((filterResData === null || !filterResData.length)
        ? { ...MODAL_CREATE_REPORT['checkNoCustomfields'], media: media }
        : { ...MODAL_CREATE_REPORT['checkImportCustomfields'], media: media });
      setIsModalOpen(true);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (origiCustomData.length > 0) {
      setIsDataReady(prev => ({ ...prev, customConversions: true }));
    }
  }, [origiCustomData])

  // 若為編輯頁且有選擇廣告活動，自動呼叫廣告活動API
  useEffect(() => {
    if (!entryPath.includes('edit') || selectedAdAccounts?.length === 0) return;

    const isCampaignFetchRequired = currentActiveData.campaignList?.length > 0 &&
      !currentActiveData.campaignList.includes('all');

    if (isCampaignFetchRequired) {
      fetchData(apiList[MEDIA_DATA[media].api.getCampaign], 'campaignList', (resData) => {
        handleCampaignData(resData, 'auto');
      })
    } else {
      setIsDataReady(prev => ({ ...prev, campaignList: true }));
    }
  }, [currentActiveData.campaignList, entryPath, selectedAdAccounts])

  // 若為編輯頁且有選擇轉換動作，自動呼叫轉換動作API
  useEffect(() => {
    if (!entryPath.includes('edit') || selectedAdAccounts?.length === 0) return;

    const hasValidCustomFields = currentActiveData.fields.some(el => el.cid !== null);

    if (hasValidCustomFields) {
      fetchData(apiList[MEDIA_DATA[media].api.customFields], 'customConversions', (resData) => {
        handleCustomData(resData, 'auto');
      })
    } else {
      setIsDataReady(prev => ({ ...prev, customConversions: true }));
    }
  }, [currentActiveData.fields, entryPath, selectedAdAccounts])

  return {
    // 廣告帳戶
    cidList,
    selectedAdAccounts,
    setSelectedAdAccounts,

    // 廣告活動
    campaignList,
    setCampaignList,
    selectedCampaign,
    setSelectedCampaign,
    handleCampaignData,

    // 自訂轉換
    origiCustomData,
    setOrigiCustomData,
    selectedCustomFields,
    setSelectedCustomFields,
    handleCustomData,

    selectedOptionFields,
    setSelectedOptionFields,
    fetchData
  }
}

export default useReportData;